import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/mode-toggle";
import { useNavigate, useParams } from "react-router-dom";
import { AiOutlineLoading3Quarters, AiOutlineQuestionCircle } from "react-icons/ai";
import Logo from "@/components/logo";
import { useAuth } from "../context/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MagicLinkLanding = () => {
    const { tokenId, tokenValue } = useParams();
    const navigate = useNavigate();
    const { verifyCode, isAuthLoading } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const hasVerified = useRef(false);

    const handleContinue = async () => {
        if (hasVerified.current || isAuthLoading) return;
        hasVerified.current = true;
        setIsLoading(true);
        setError(null);
        
        try {
            const result = await verifyCode(tokenId, tokenValue);
            
            if (result.success) {
                // Small delay to allow user to see the spinner/transition
                setTimeout(() => {
                    navigate(result.needsOnboarding ? '/onboarding' : '/today');
                }, 500);
            } else {
                setError(result.error);
                hasVerified.current = false;
                setIsLoading(false);
            }
        } catch (err) {
            setError('An error occurred during verification. Please try again later.');
            hasVerified.current = false;
            setIsLoading(false);
        }
    };

    const handleGoBack = () => navigate('/login');

    return (
        <div className="relative min-h-screen flex flex-col items-center justify-center p-4 bg-background">
            <div className="absolute top-8 left-8 z-10">
                <Logo onClick={() => navigate('/')} className="cursor-pointer" />
            </div>
            <div className="absolute top-8 right-8 z-10">
                <ModeToggle />
            </div>

            <div
                className="border w-full md:max-w-md bg-card text-card-foreground rounded-lg shadow-lg p-4 md:p-8 space-y-4"
            >
                <h1 className="text-left text-2xl md:text-4xl">Continue logging in</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                    We're verifying this link to ensure it's you. Please click below to continue logging in.
                </p>
                {error && (
                    <div className="text-sm text-red-700 dark:text-red-500">
                        {error}
                    </div>
                )}
                <div className="space-y-4">
                    <Button
                        className="w-full h-12 bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:opacity-90 transition-opacity"
                        onClick={handleContinue}
                        disabled={isLoading || isAuthLoading}
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center">
                                <AiOutlineLoading3Quarters className="mr-2 h-5 w-5 animate-spin" />
                                Authenticating...
                            </span>
                        ) : (
                            "Log In Now"
                        )}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleGoBack}
                        className="w-full"
                    >
                        Cancel
                    </Button>
                </div>
            </div>

            {/* Tooltip Section */}
            <TooltipProvider delayDuration={0}>
                <div className="mt-4">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="flex items-center cursor-default text-muted-foreground hover:text-primary">
                                <AiOutlineQuestionCircle className="mr-1 h-5 w-5" />
                                <span className="text-sm">Why the extra step?</span>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                            <p className="text-sm text-left">
                                Some anti-phishing systems open login links in emails, which is why we require this two-step process.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </TooltipProvider>
        </div>
    );
};

export default MagicLinkLanding; 